import {Injectable} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {fromEvent, Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {PreferencesService} from "../preferences/preferences.service";

@Injectable()
export class OfflineQueueInterceptor implements HttpInterceptor {
  private requestQueue: HttpRequest<any>[] = [];
  private readonly STORAGE_KEY = 'failed_requests';

  constructor(
    private http: HttpClient,
    private preferencesService: PreferencesService,
  ) {
    fromEvent(window, 'online').subscribe(() => {
      this.processQueue();
    });

    this.loadQueueFromStorage();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log("intercept")
    if (!navigator.onLine) {
      console.log("intercept: offline")
      this.enqueueRequest(req);
      return throwError('Нет подключения к сети. Запрос сохранен для повторной отправки.');
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldRetryRequest(error)) {
          console.log('Добавляем запрос в очередь из-за ошибки:', error.status, req.url);
          this.enqueueRequest(req);
        } else {
          console.log('Не добавляем запрос в очередь (ошибка клиента):', error.status, req.url);
        }
        return throwError(error);
      })
    );
  }

  private shouldRetryRequest(error: HttpErrorResponse): boolean {
    if (error.status === 0) {
      return true;
    }

    if (error.error instanceof ErrorEvent) {
      return true;
    }

    const retryableServerErrors = [
      408, // Request Timeout
      429, // Too Many Requests
      502, // Bad Gateway
      503, // Service Unavailable
      504, // Gateway Timeout
      520, // Web Server Returned an Unknown Error
      521, // Web Server Is Down
      522, // Connection Timed Out
      523, // Origin Is Unreachable
      524  // A Timeout Occurred
    ];

    if (retryableServerErrors.includes(error.status)) {
      return true;
    }

    // Общие серверные ошибки 5xx (кроме уже обработанных)
    if (error.status >= 500) {
      return true;
    }

    return false;
  }

  private isNetworkError(error: HttpErrorResponse): boolean {
    return this.shouldRetryRequest(error);
  }

  private enqueueRequest(request: HttpRequest<any>): void {
    const isDuplicate = this.requestQueue.some(req =>
      req.url === request.url &&
      req.method === request.method &&
      JSON.stringify(req.body) === JSON.stringify(request.body)
    );

    if (isDuplicate) {
      console.log('Запрос уже в очереди, пропускаем:', request.url);
      return;
    }

    const serializedRequest = {
      url: request.url,
      method: request.method,
      headers: this.serializeHeaders(request.headers),
      body: request.body,
      params: request.params.toString(),
      timestamp: Date.now()
    };

    this.requestQueue.push(request);
    this.saveQueueToStorage(serializedRequest);
    console.log('Запрос добавлен в очередь:', request.url, 'Всего в очереди:', this.requestQueue.length);
  }

  private serializeHeaders(headers: any): any {
    const serialized: any = {};
    headers.keys().forEach((key: string) => {
      serialized[key] = headers.get(key);
    });
    return serialized;
  }

  private saveQueueToStorage(request: any): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    const requests = stored ? JSON.parse(stored) : [];
    requests.push(request);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(requests));
  }

  private loadQueueFromStorage(): void {
    console.log("load from storage")
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      const requests = JSON.parse(stored);
      console.log("intercept: len=" + requests.length)

      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      const validRequests = requests.filter((req: any) =>
        !req.timestamp || req.timestamp > oneDayAgo
      );

      validRequests.forEach((req: any) => {
        try {
          const httpRequest = new HttpRequest(req.method, req.url, req.body, {
            headers: req.headers,
            params: req.params
          });
          this.requestQueue.push(httpRequest);
        } catch (error) {
          console.error('Ошибка при восстановлении запроса из хранилища:', error);
        }
      });

      if (validRequests.length !== requests.length) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(validRequests.map(req => ({
          url: req.url,
          method: req.method,
          headers: req.headers,
          body: req.body,
          params: req.params,
          timestamp: req.timestamp
        }))));
      }
    }
  }

  private processQueue(): void {
    console.log('Обработка очереди запросов, в очереди:', this.requestQueue.length);

    if (!navigator.onLine || this.requestQueue.length === 0) {
      return;
    }

    const queue = [...this.requestQueue];
    this.requestQueue = [];
    localStorage.removeItem(this.STORAGE_KEY);

    queue.forEach((request, index) => {
      setTimeout(() => {
        this.retryRequest(request);
      }, index * 100);
    });
  }

  private async retryRequest(request: HttpRequest<any>): Promise<void> {
    const maxRetries = 3;
    let lastError: any;

    console.log('Начинаем повторную отправку запроса:', request.url);

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Попытка ${attempt}/${maxRetries} для ${request.url}`);

        const currentToken = this.preferencesService.appPreferences?.token;

        let modifiedRequest = request;

        if (currentToken) {
          modifiedRequest = request.clone({
            headers: request.headers
              // .delete('Authorization') // Удаляем старый токен
              // .set('Authorization', "Bearer " + this.preferencesService.appPreferences.token) // Устанавливаем актуальный
          });
        }

        const response = await this.http.request(modifiedRequest).toPromise();

        console.log('✅ Запрос успешно выполнен:', request.url);
        return;

      } catch (error) {
        lastError = error;
        const httpError = error as HttpErrorResponse;

        console.log(`❌ Ошибка при попытке ${attempt}:`, request.url, httpError.status);

        if (httpError.status >= 400 && httpError.status < 500 &&
          ![408, 429].includes(httpError.status)) {
          console.log('🚫 Ошибка клиента, прекращаем попытки:', request.url, httpError.status);
          return;
        }

        if (this.shouldRetryRequest(httpError) && attempt < maxRetries) {
          const delay = 1000 * Math.pow(2, attempt - 1);
          console.log(`🔄 Повтор через ${delay}ms для:`, request.url);
          await this.delay(delay);
          continue;
        }

        break;
      }
    }

    console.error('❌ Все попытки исчерпаны для:', request.url, lastError);

    if (lastError && this.shouldRetryRequest(lastError)) {
      console.log('🔄 Возвращаем запрос в очередь для следующей попытки:', request.url);
      this.enqueueRequest(request);
    }
  }


  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
